/**
 * How a channel score compares with human writing (DESIGN UD-24, ED-126; docs/plan/HUMAN-REFERENCE-DESIGN.md).
 *
 * Pure: no DOM, no Node imports. The reference is `src/web/data/human-reference.json`, one row per human document of the
 * CV slice (`[stratum index, words, score of each channel in tenths or null]`). The browser pools any selection of strata
 * and any length window exactly, so the rows are kept whole.
 *
 * The statistic (design §4), for a score x (rounded to tenths, like the reference) and a pool of n human scores:
 *   below = #(human < x), equal = #(human = x)
 *   percentile = 100 (below + equal / 2) / n                    (mid-rank)
 *   tLow = (below + equal + 1) / (n + 1), tHigh = (n - below + 1) / (n + 1), tail = 100 min(tLow, tHigh)
 * The +1 (the permutation-test convention) means n documents can never claim more than 1/(n + 1): 73 documents reach
 * at best 1.35% (yellow), red needs 199.
 */
import type { ChannelId } from '../engine/channels/types.ts';

export const HUMAN_REFERENCE_SCHEMA = 'provenance.human-reference/1';
/** The length window: from half to twice the text's words, inclusive. */
export const WINDOW: readonly [number, number] = [0.5, 2];
/** Fewer human documents than this: the cell is unmeasured (DESIGN CD-4). */
export const MIN_N = 30;
/** Percent: a tail at or above this is within the human range (the middle 90%). */
export const GREEN_TAIL = 5;
/** Percent: a tail at or below this is far outside the human range (red). */
export const RED_TAIL = 0.5;

export interface HumanStratum {
	id: string;
	n: number;
	translation: boolean;
}

export interface HumanReference {
	schema: typeof HUMAN_REFERENCE_SCHEMA;
	fingerprint: string;
	builtAt: string;
	source: string;
	channels: ChannelId[];
	strata: HumanStratum[];
	/** `[stratum index, words, ...scoreTenths]`: scoreTenths is `Math.round(score * 10)`, or null where the channel did not score. */
	docs: Array<Array<number | null>>;
	/**
	 * Each document's CV fold (0 to 4), aligned with `docs`: a human document's own band counts are measured against the
	 * reference without its fold (UD-26), as the held-out check does. Absent from a reference built before UD-26.
	 */
	folds?: number[];
}

export type HumanBand = 'within' | 'outside' | 'far';

export type HumanComparison =
	/** the text's own channel did not score (score null) */
	| { status: 'not-applicable' }
	/** fewer than MIN_N human documents in the window (n may be 0) */
	| { status: 'unmeasured'; n: number }
	| {
			status: 'measured';
			n: number;
			/** 0-100, mid-rank */
			percentile: number;
			/** percent, one-sided, +1 corrected: the farther the smaller */
			tail: number;
			side: 'above' | 'below';
			band: HumanBand;
			/** 0 (red) to 120 (green) */
			hue: number;
			/** 'above' when x is above every human score in the pool, 'below' when below every one, else null */
			beyond: 'above' | 'below' | null;
			/** the pool's middle 90% (5th to 95th percentile, linear interpolation) */
			range: [number, number];
			median: number;
			/** |x - median| in units of the middle-90% width (at least 0.1) */
			distance: number;
	  };

/** Every stratum that is not a translation stratum (all ten original-English categories). */
export function defaultStrata(ref: HumanReference): string[] {
	return ref.strata.filter((s) => !s.translation).map((s) => s.id);
}

/** The reference's documents ordered by words, with the words alone beside them for binary search. */
interface Index {
	order: Array<Array<number | null>>;
	words: Float64Array;
	/** channel id to its column in a row */
	column: Map<string, number>;
	/** stratum id to its index */
	stratumIndex: Map<string, number>;
	pools: Map<string, number[]>;
}

const INDEX = new WeakMap<HumanReference, Index>();
/** A memo of pools per reference; cleared when it grows past this many entries (a selection change touches 37 × 2). */
const MEMO_MAX = 512;

function indexOf(ref: HumanReference): Index {
	let ix = INDEX.get(ref);
	if (ix) return ix;
	const order = ref.docs.slice().sort((a, b) => (a[1] as number) - (b[1] as number));
	const words = new Float64Array(order.length);
	for (let i = 0; i < order.length; i++) words[i] = (order[i] as Array<number | null>)[1] as number;
	const column = new Map<string, number>();
	ref.channels.forEach((c, i) => column.set(c, i + 2));
	const stratumIndex = new Map<string, number>();
	ref.strata.forEach((s, i) => stratumIndex.set(s.id, i));
	ix = { order, words, column, stratumIndex, pools: new Map() };
	INDEX.set(ref, ix);
	return ix;
}

/** First index in sorted `a` whose value is >= x (so `lowerBound` = #(a < x)). */
function lowerBound(a: ArrayLike<number>, x: number): number {
	let lo = 0;
	let hi = a.length;
	while (lo < hi) {
		const mid = (lo + hi) >>> 1;
		if ((a[mid] as number) < x) lo = mid + 1;
		else hi = mid;
	}
	return lo;
}

/** First index in sorted `a` whose value is > x (so `upperBound` = #(a <= x)). */
function upperBound(a: ArrayLike<number>, x: number): number {
	let lo = 0;
	let hi = a.length;
	while (lo < hi) {
		const mid = (lo + hi) >>> 1;
		if ((a[mid] as number) <= x) lo = mid + 1;
		else hi = mid;
	}
	return lo;
}

/** The memoised pool (shared: callers must not modify it). */
function poolOf(
	ref: HumanReference,
	channel: ChannelId,
	words: number,
	strata: ReadonlySet<string>
): readonly number[] {
	const ix = indexOf(ref);
	const col = ix.column.get(channel);
	if (col === undefined || !Number.isFinite(words) || words < 0) return [];
	const key = `${col}|${words}|${[...strata].sort().join('\u0001')}`;
	const hit = ix.pools.get(key);
	if (hit) return hit;
	const selected = new Uint8Array(ref.strata.length);
	for (const id of strata) {
		const i = ix.stratumIndex.get(id);
		if (i !== undefined) selected[i] = 1;
	}
	const from = lowerBound(ix.words, words * WINDOW[0]);
	const to = upperBound(ix.words, words * WINDOW[1]);
	const vals: number[] = [];
	for (let i = from; i < to; i++) {
		const row = ix.order[i] as Array<number | null>;
		if (selected[row[0] as number] !== 1) continue;
		const t = row[col];
		if (t === null || t === undefined) continue;
		vals.push(t);
	}
	const f = Float64Array.from(vals);
	f.sort();
	const pool = Array.from(f, (t) => t / 10);
	if (ix.pools.size >= MEMO_MAX) ix.pools.clear();
	ix.pools.set(key, pool);
	return pool;
}

/**
 * The scores (units of the score, i.e. tenths / 10) of the human documents whose words lie in
 * [words * 0.5, words * 2] (inclusive), in a selected stratum, where `channel` scored; sorted ascending.
 * Documents are pooled document-weighted (the union of the selected strata, no per-stratum weights).
 */
export function humanPool(
	ref: HumanReference,
	channel: ChannelId,
	words: number,
	strata: ReadonlySet<string>
): number[] {
	return poolOf(ref, channel, words, strata).slice();
}

/** Quantile of sorted `a` at p in [0, 1], linear interpolation between order statistics (R type 7). */
function quantile(a: readonly number[], p: number): number {
	const h = (a.length - 1) * p;
	const lo = Math.floor(h);
	const v = a[lo] as number;
	const next = a[lo + 1];
	return next === undefined ? v : v + (h - lo) * (next - v);
}

/** Within the human range: the tail (percent) is at least GREEN_TAIL; far outside at most RED_TAIL; else outside. */
export function tailBand(t: number): HumanBand {
	if (t >= GREEN_TAIL) return 'within';
	if (t <= RED_TAIL) return 'far';
	return 'outside';
}

/**
 * Green (120) within the range. Outside it, the hue starts at yellow-green (90) just past the edge, so green always means
 * within, and runs continuously through yellow (60 at t = 5/10^(1/3), about 2.32) to red (0) at RED_TAIL:
 * 90 (1 − log10(5 / t)).
 */
export function tailHue(t: number): number {
	if (t >= GREEN_TAIL) return 120;
	if (t <= RED_TAIL) return 0;
	const s = Math.min(1, Math.max(0, Math.log10(GREEN_TAIL / t)));
	return 90 * (1 - s);
}

/**
 * The comparison of one score with an already pooled, ascending human sample. `score` is rounded to tenths first,
 * like the reference. Fewer than MIN_N pooled scores: unmeasured.
 */
export function compareToPool(pool: readonly number[], score: number | null): HumanComparison {
	if (score === null || !Number.isFinite(score)) return { status: 'not-applicable' };
	const n = pool.length;
	if (n < MIN_N) return { status: 'unmeasured', n };
	const x = Math.round(score * 10) / 10;
	const below = lowerBound(pool, x);
	const equal = upperBound(pool, x) - below;
	const percentile = (100 * (below + equal / 2)) / n;
	const tLow = (below + equal + 1) / (n + 1);
	const tHigh = (n - below + 1) / (n + 1);
	const tail = 100 * Math.min(tLow, tHigh);
	// an exact tie means the score sits at the median (percentile 50): 'above' by the percentile >= 50 rule
	const side: 'above' | 'below' = tHigh < tLow ? 'above' : tLow < tHigh ? 'below' : 'above';
	const beyond: 'above' | 'below' | null =
		below === n ? 'above' : below + equal === 0 ? 'below' : null;
	const range: [number, number] = [quantile(pool, 0.05), quantile(pool, 0.95)];
	const median = quantile(pool, 0.5);
	const distance = Math.abs(x - median) / Math.max(range[1] - range[0], 0.1);
	return {
		status: 'measured',
		n,
		percentile,
		tail,
		side,
		band: tailBand(tail),
		hue: tailHue(tail),
		beyond,
		range,
		median,
		distance,
	};
}

/**
 * Where `score` (a channel's score, oriented so that higher is more machine-like) falls among the human documents of
 * about this length (`words`) in the selected strata. `score` null: not applicable (the channel abstained).
 */
export function compareToHuman(
	ref: HumanReference,
	channel: ChannelId,
	score: number | null,
	words: number,
	strata: ReadonlySet<string>
): HumanComparison {
	if (score === null || !Number.isFinite(score)) return { status: 'not-applicable' };
	return compareToPool(poolOf(ref, channel, words, strata), score);
}

const RANK = { measured: 0, unmeasured: 1, 'not-applicable': 2 } as const;

/**
 * A sort comparator, farthest from the human range first: measured cells before unmeasured before not-applicable;
 * measured by tail ascending, then distance from the median descending. A tie returns 0 (the sort is stable).
 */
export function compareHuman(a: HumanComparison, b: HumanComparison): number {
	const ra = RANK[a.status];
	const rb = RANK[b.status];
	if (ra !== rb) return ra < rb ? -1 : 1;
	if (a.status !== 'measured' || b.status !== 'measured') return 0;
	if (a.tail !== b.tail) return a.tail < b.tail ? -1 : 1;
	if (a.distance !== b.distance) return a.distance > b.distance ? -1 : 1;
	return 0;
}

/* ---------------- how many channels are outside, against human documents (UD-26) ---------------- */

/** How many of a text's channels were measured, are outside the human range (yellow or red) and far outside it (red). */
export interface BandCounts {
	measured: number;
	outside: number;
	far: number;
}

export function countBands(comparisons: Iterable<HumanComparison>): BandCounts {
	const c: BandCounts = { measured: 0, outside: 0, far: 0 };
	for (const x of comparisons) {
		if (x.status !== 'measured') continue;
		c.measured++;
		if (x.band !== 'within') c.outside++;
		if (x.band === 'far') c.far++;
	}
	return c;
}

/**
 * The text's counts placed among the human documents of about its length in the selected strata. `shareFar` is the share
 * of those documents with at least as many red channels, `shareOutside` with at least as many channels outside the range,
 * each document's own counts measured against the reference without its fold (the held-out check's method). Fewer than
 * MIN_N such documents, or a reference without folds: unmeasured.
 */
export type HumanCountSummary =
	| ({ status: 'measured'; n: number; shareFar: number; shareOutside: number } & BandCounts)
	| ({ status: 'unmeasured'; n: number } & BandCounts);

interface FoldIndex {
	/** for each position of the words-sorted order: the document's index in `ref.docs` and its fold */
	docOf: Int32Array;
	foldOf: Int8Array;
	counts: Map<string, BandCounts>;
}

const FOLD_INDEX = new WeakMap<HumanReference, FoldIndex>();
const COUNTS_MAX = 20000;

function foldIndexOf(ref: HumanReference): FoldIndex | null {
	const folds = ref.folds;
	if (!folds || folds.length !== ref.docs.length) return null;
	let fx = FOLD_INDEX.get(ref);
	if (fx) return fx;
	const ix = indexOf(ref);
	const pos = new Map(ref.docs.map((row, i) => [row, i] as const));
	const docOf = new Int32Array(ix.order.length);
	const foldOf = new Int8Array(ix.order.length);
	ix.order.forEach((row, k) => {
		const i = pos.get(row) as number;
		docOf[k] = i;
		foldOf[k] = folds[i] as number;
	});
	fx = { docOf, foldOf, counts: new Map() };
	FOLD_INDEX.set(ref, fx);
	return fx;
}

const strataKey = (strata: ReadonlySet<string>): string => [...strata].sort().join('\u0001');

/**
 * One human document's band counts (`index` into `ref.docs`): each of its channels against the selected strata's
 * documents from half to twice its words, its own fold left out, by the same tail and bands as `compareToPool`.
 * Null for a reference without folds.
 */
export function humanDocCounts(
	ref: HumanReference,
	index: number,
	strata: ReadonlySet<string>
): BandCounts | null {
	const fx = foldIndexOf(ref);
	const row = ref.docs[index];
	if (!fx || !row) return null;
	const key = `${strataKey(strata)}|${index}`;
	const hit = fx.counts.get(key);
	if (hit) return hit;
	const ix = indexOf(ref);
	const selected = new Uint8Array(ref.strata.length);
	for (const id of strata) {
		const i = ix.stratumIndex.get(id);
		if (i !== undefined) selected[i] = 1;
	}
	const words = row[1] as number;
	const fold = (ref.folds as number[])[index] as number;
	const from = lowerBound(ix.words, words * WINDOW[0]);
	const to = upperBound(ix.words, words * WINDOW[1]);
	const c: BandCounts = { measured: 0, outside: 0, far: 0 };
	for (const col of ix.column.values()) {
		const x = row[col];
		if (x === null || x === undefined) continue;
		let n = 0;
		let below = 0;
		let equal = 0;
		for (let k = from; k < to; k++) {
			if (fx.foldOf[k] === fold) continue;
			const other = ix.order[k] as Array<number | null>;
			if (selected[other[0] as number] !== 1) continue;
			const t = other[col];
			if (t === null || t === undefined) continue;
			n++;
			if (t < x) below++;
			else if (t === x) equal++;
		}
		if (n < MIN_N) continue;
		const tail = (100 * Math.min(below + equal + 1, n - below + 1)) / (n + 1);
		const band = tailBand(tail);
		c.measured++;
		if (band !== 'within') c.outside++;
		if (band === 'far') c.far++;
	}
	if (fx.counts.size >= COUNTS_MAX) fx.counts.clear();
	fx.counts.set(key, c);
	return c;
}

/**
 * The indices (into `ref.docs`) of the human documents from half to twice `words` in the selected strata: the documents
 * whose own counts `humanCounts` compares with. A caller may compute their `humanDocCounts` ahead, in slices.
 */
export function humanWindowDocs(
	ref: HumanReference,
	words: number,
	strata: ReadonlySet<string>
): number[] {
	const fx = foldIndexOf(ref);
	if (!fx || !Number.isFinite(words) || words < 0) return [];
	const ix = indexOf(ref);
	const selected = new Uint8Array(ref.strata.length);
	for (const id of strata) {
		const i = ix.stratumIndex.get(id);
		if (i !== undefined) selected[i] = 1;
	}
	const from = lowerBound(ix.words, words * WINDOW[0]);
	const to = upperBound(ix.words, words * WINDOW[1]);
	const out: number[] = [];
	for (let k = from; k < to; k++)
		if (selected[(ix.order[k] as Array<number | null>)[0] as number] === 1)
			out.push(fx.docOf[k] as number);
	return out;
}

/** The text's band counts among the human documents of about its length in the selected strata (see HumanCountSummary). */
export function humanCounts(
	ref: HumanReference,
	comparisons: Iterable<HumanComparison>,
	words: number,
	strata: ReadonlySet<string>
): HumanCountSummary {
	const own = countBands(comparisons);
	let n = 0;
	let atLeastFar = 0;
	let atLeastOutside = 0;
	for (const i of humanWindowDocs(ref, words, strata)) {
		const counts = humanDocCounts(ref, i, strata);
		if (!counts || counts.measured === 0) continue;
		n++;
		if (counts.far >= own.far) atLeastFar++;
		if (counts.outside >= own.outside) atLeastOutside++;
	}
	if (n < MIN_N) return { status: 'unmeasured', n, ...own };
	return {
		status: 'measured',
		n,
		shareFar: atLeastFar / n,
		shareOutside: atLeastOutside / n,
		...own,
	};
}
